import { Form, Formik, FormikConfig } from 'formik';

import { Button } from 'components/atoms';
import {
  DescriptiveInputWrapper,
  FormInput,
  InfoBox,
} from 'components/molecules';
import i18next from 'i18next';
import _ from 'lodash';
import { useStoreState } from 'providers/StoreStateContext';
import { useTranslation } from 'react-i18next';
import { PiFloppyDisk, PiProhibit } from 'react-icons/pi';
import { IClientModuleConfigurationForm } from 'services/settings/client-module/types';
import { useGetContractTemplateLightList } from 'services/templates/contract-template';
import { BUTTON_VARIANT } from 'types/components/button/button.interface';
import { II18nextPrefix } from 'types/components/components.interface';
import { getImageSize } from 'utils/components/inputs/image';
import { booleanSelectOptions } from 'utils/components/inputs/select';
import { object, string } from 'yup';
import { IFormProps } from '../types';

export const clientModuleConfigurationInitialValues: IClientModuleConfigurationForm =
  {
    contractTemplateId: '',
    clientName: '',
    useMagellupLogo: true,
  };

const clientModuleConfigurationValidator = object().shape({
  contractTemplateId: string().required(i18next.t('formik-error.required')),
  clientName: string().required(i18next.t('formik-error.required')),
});

export interface ISettingsFormProps
  extends FormikConfig<IClientModuleConfigurationForm>,
    IFormProps,
    II18nextPrefix {}

const ApplicationSettingsForm = ({
  i18nextPrefix,
  topbarContent,
  tittle,
  status,
  initialValues = clientModuleConfigurationInitialValues,
  disableTabs,
  backPath,
  customBackItem,
  ...props
}: ISettingsFormProps) => {
  const { t } = useTranslation();

  const contractTemplates = useGetContractTemplateLightList();

  const { ownerLogo } = useStoreState();

  return (
    <Formik
      {...props}
      validateOnChange
      validateOnBlur
      validateOnMount
      validationSchema={clientModuleConfigurationValidator}
      initialStatus={status?.displayMode}
      enableReinitialize
      initialValues={initialValues}>
      {({ setValues, values, status, errors, setFieldValue }) => (
        <>
          <Form className='flex w-full h-full gap-[30px] flex-wrap'>
            <div className='flex flex-col gap-[30px] w-[370px]'>
              <DescriptiveInputWrapper
                header={t('setting.client-module.brand')}
                description={t('setting.client-module.brandDescription')}>
                <FormInput.Text
                  name='clientName'
                  label={t('input.label.name')}
                />
                <FormInput.Select
                  name='useMagellupLogo'
                  label={t('input.label.useMagellupLogo')}
                  options={booleanSelectOptions}
                  isClearable={false}
                />
                <div className='flex w-full justify-center p-30'>
                  <InfoBox.Image
                    fullResolutionUrl={
                      values.useMagellupLogo
                        ? ownerLogo?.pictureLink
                        : values.clientLogoPath
                    }
                    previewUrl={
                      values.useMagellupLogo
                        ? ownerLogo?.pictureLink
                        : values.clientLogoPath
                    }
                    accept='image/jpg, image/jpeg, image/png'
                    editable={!values.useMagellupLogo}
                    onChange={file => setFieldValue('file', file)}
                    onCancel={action => {
                      setFieldValue('file', undefined);
                      action.close();
                    }}
                    //   onDelete={
                    //     initialValues.clientLogoPath
                    //       ? action => {
                    //           setFieldValue('clientLogoPath', '');
                    //           setFieldValue('file', undefined);
                    //           action.close();
                    //         }
                    //       : undefined
                    //   }
                    imageSize={getImageSize(500)}
                  />
                </div>
              </DescriptiveInputWrapper>
              <DescriptiveInputWrapper
                header={t('setting.client-module.contractTemplate')}
                description={t(
                  'setting.client-module.contractTemplateDescription'
                )}>
                <FormInput.Select
                  name='contractTemplateId'
                  label={t('input.label.contract-template')}
                  options={contractTemplates.data?.map(c => ({
                    value: c.id,
                    label: c.title,
                  }))}
                  isClearable={false}
                />
              </DescriptiveInputWrapper>
            </div>

            {!_.isEqual(initialValues, values) && (
              <div className='buttons'>
                <Button
                  variant={BUTTON_VARIANT.warning}
                  onClick={() => setValues(initialValues)}>
                  <Button.Icon icon={<PiProhibit transform='scale(-1,1)' />} />
                  <Button.Text text={t(`enum.TEMPLATE_TYPE_SETTINGS.cancle`)} />
                </Button>
                <Button type='submit' variant={BUTTON_VARIANT.submit}>
                  <Button.Icon
                    icon={<PiFloppyDisk transform='scale(-1,1)' />}
                  />
                  <Button.Text text={t(`enum.TEMPLATE_TYPE_SETTINGS.submit`)} />
                </Button>
              </div>
            )}
          </Form>
        </>
      )}
    </Formik>
  );
};

export default ApplicationSettingsForm;
